import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { Collapse } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  MaximizeIcon,
  MinimizeIcon,
} from '@iconicicons/react';
import { BelowMapTabProps } from './BelowMapTab';

type BelowMapComponent = React.ComponentElement<
  React.PropsWithRef<BelowMapTabProps>,
  React.Component<React.PropsWithRef<BelowMapTabProps>>
>;

export type BelowMapContextType = {
  components: Record<string, BelowMapComponent>;
  fullScreen: boolean;
  isOpen: boolean;
  activeTab: string;
  addComponents: (objectsToAdd: Record<string, BelowMapComponent>) => void;
  removeComponents: (keysToRemove: string[]) => void;
  setFullScreen: React.Dispatch<React.SetStateAction<boolean>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
};

export const BelowMapContext = createContext<BelowMapContextType>({
  components: {},
  fullScreen: false,
  isOpen: true,
  activeTab: '0',
  addComponents: () => {},
  removeComponents: () => {},
  setFullScreen: () => {},
  setOpen: () => {},
  setActiveTab: () => {},
});

export type BelowMapProviderProps = {};

const BelowMapProvider: React.FC<
  React.PropsWithChildren<BelowMapProviderProps>
> = (props) => {
  const { children } = props;
  const [components, setComponents] = useState<
    Record<string, BelowMapComponent>
  >({});
  const navigate = useNavigate();
  const [fullScreen, setFullScreen] = useState(false);
  const [isOpen, _setOpen] = useState(true);
  const [activeTab, _setActiveTab] = useState('0');

  const setActiveTab: BelowMapContextType['setActiveTab'] = useCallback(
    (value) => {
      _setActiveTab((currentActiveTab) => {
        const tab =
          typeof value === 'function' ? value(currentActiveTab) : value;
        const currentComponent = components[currentActiveTab];
        const newComponent = components[tab];

        const to = components[tab]?.props?.to;
        if (to) {
          navigate(to);
        }
        currentComponent?.props?.onTabBlur?.();
        newComponent?.props?.onTabFocus?.();
        return tab;
      });
    },
    [components, navigate],
  );

  const addComponents: BelowMapContextType['addComponents'] = (
    objectsToAdd,
  ) => {
    setComponents((components) => {
      const newComponents = { ...components, ...objectsToAdd };
      const activeComponent = React.Children.toArray(
        Object.values(components) as unknown as React.ReactNode[],
      ).some(
        (component) =>
          // @ts-expect-error
          component.props?.activeTab === true,
      )
        ? null
        : React.Children.toArray(
            Object.values(objectsToAdd) as unknown as React.ReactNode[],
          ).find(
            (component) =>
              // @ts-expect-error
              component.props?.activeTab ===
                // @ts-expect-error
                true && !component[component.props?.contextKey], // @ts-expect-error
          )?.props?.contextKey;

      if (activeComponent) {
        setActiveTab(activeComponent);
      } else {
        if (
          !Object.keys(newComponents).includes(activeTab) &&
          Object.keys(newComponents).length > 0
        ) {
          setActiveTab(Object.keys(newComponents)[0]);
        }
      }
      return { ...newComponents };
    });
  };

  const removeComponents: BelowMapContextType['removeComponents'] = (
    keysToRemove,
  ) => {
    setComponents((components) => {
      keysToRemove.forEach((key) => delete components[key]);
      const newComponents = { ...components };
      if (
        keysToRemove.includes(activeTab) &&
        Object.keys(newComponents).length > 0
      ) {
        setActiveTab(Object.keys(newComponents)[0]);
      }
      return newComponents;
    });
  };

  const setOpen: BelowMapContextType['setOpen'] = (value) => {
    if (typeof value === 'function') {
      _setOpen(value);
      return;
    }
    if (Boolean(value) === Boolean(isOpen)) {
      return;
    }
    _setOpen(Boolean(value));
  };

  return (
    <BelowMapContext.Provider
      value={{
        components,
        fullScreen,
        isOpen,
        activeTab,
        addComponents,
        removeComponents,
        setFullScreen,
        setOpen,
        setActiveTab,
      }}
    >
      {children as unknown as React.ReactNode}
    </BelowMapContext.Provider>
  );
};

const useBelowMap = () => {
  const context = useContext(BelowMapContext);
  if (context === undefined) {
    throw new Error('BelowMap must be used within a BelowMapProvider');
  }
  return context;
};

const BelowMapSetter: React.FC<React.PropsWithChildren> = (props) => {
  const { children } = props;
  const { addComponents, removeComponents } = useBelowMap();
  const childrenKeys: string[] = useMemo(
    () =>
      React.Children.toArray(children).map(
        // @ts-expect-error
        (child) => child?.props?.contextKey ?? child.toString(),
      ),
    [children],
  );

  useEffect(() => {
    if (children) {
      const childrenToObject = React.Children.toArray(children).reduce<
        Record<string, BelowMapComponent>
      >((acc, child) => {
        // @ts-expect-error
        acc[child.props.contextKey] = child;
        return acc;
      }, {});
      addComponents(childrenToObject);
    }
  }, [childrenKeys]);

  useEffect(() => {
    return () => {
      removeComponents(childrenKeys);
    };
  }, [childrenKeys.join('\\')]);

  return null; // should it be a hook if it returns nothing? Only thing is I want it to behave like a normal component otherwise
};

const BelowMapDisplay: React.FC = () => {
  const {
    components,
    fullScreen,
    setFullScreen,
    activeTab,
    isOpen,
    setActiveTab,
    setOpen,
  } = useBelowMap();

  const setSelected = (
    _evt: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    key: string,
  ) => {
    if (!isOpen) {
      setOpen(true);
    }

    if (isOpen && activeTab === key) {
      setOpen(false);
    } else {
      setActiveTab(key);
    }
    return false;
  };

  const containerStyles = useMemo<{
    open: React.CSSProperties;
    closed: React.CSSProperties;
  }>(() => {
    return {
      open: {
        height: fullScreen ? '100%' : '50%',
        position: 'relative',
        minHeight: '42px',
        zIndex: 4,
      },
      closed: {
        maxHeight: '50%',
        height: 'auto',
        position: 'relative',
        minHeight: '42px',
        zIndex: 4,
      },
    };
  }, [fullScreen]);

  if (Object.keys(components).length === 0) {
    return null;
  }

  return (
    <div
      style={isOpen ? containerStyles.open : containerStyles.closed}
      className={`d-flex flex-grow-0 flex-column border ${
        isOpen ? 'resizable-top' : ''
      }`}
    >
      {isOpen && <div className="draggable-border draggable-border-top"></div>}
      <ul className="d-flex nav nav-tabs pe-3">
        {Object.keys(components)
          .sort((k1, k2) => {
            let order1: number | undefined = components[k1]?.props?.tabOrder;
            let order2: number | undefined = components[k2]?.props?.tabOrder;

            if (order1 === undefined && order2 === undefined) {
              return 0;
            }

            if (order1 === undefined && order2 !== undefined) {
              if (order2 < 0) {
                return -1;
              }

              return 1;
            }

            if (order2 === undefined && order1 !== undefined) {
              if (order1 < 0) {
                return 1;
              }

              return -1;
            }

            if (order1 !== undefined && order2 !== undefined) {
              // if order is negative, put it at the end
              order1 = order1 < 0 ? Number.MAX_SAFE_INTEGER + order1 : order1;
              order2 = order2 < 0 ? Number.MAX_SAFE_INTEGER + order2 : order2;

              return order1 - order2;
            }

            return 0;
          })
          .map((key) => {
            const component = components[key];

            return (
              <li className="nav-item" key={key}>
                <button
                  className={`nav-link ${key === activeTab ? 'active' : ''}`}
                  onClick={(evt) => setSelected(evt, key)}
                >
                  {component.props.tabName || key}
                </button>
              </li>
            );
          })}
        <button
          onClick={() => setOpen((p) => !p)}
          className="btn btn-outline-default p-1"
          style={{ marginLeft: 'auto' }}
        >
          {isOpen ? <ChevronDownIcon /> : <ChevronUpIcon />}{' '}
        </button>
        <button
          onClick={() => setFullScreen((p) => !p)}
          className="btn btn-outline-default p-1"
        >
          {fullScreen ? <MinimizeIcon /> : <MaximizeIcon />}{' '}
        </button>
      </ul>
      <Collapse
        id="below-map-context"
        isOpen={isOpen || fullScreen}
        className="overflow-auto h-100"
      >
        {Object.keys(components).map((key) => (
          <div key={key} className={key === activeTab ? 'h-100' : 'd-none'}>
            {key === activeTab || !components[key].props.unmountOnBlur
              ? (components[key] as unknown as React.ReactNode)
              : null}
          </div>
        ))}
      </Collapse>
    </div>
  );
};

export { BelowMapProvider, BelowMapDisplay, useBelowMap, BelowMapSetter };
